import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import MobileBrandHeader from "@/components/MobileBrandHeader";
import { Bus, MapPin, Clock, Users, Ticket, CheckCircle2, CalendarDays, CalendarRange } from "lucide-react";

function nextNWeekdays(n = 5, fromDate = new Date()) {
  const out = [];
  let d = new Date(fromDate);
  while (out.length < n) {
    const day = d.getDay();
    if (day >= 1 && day <= 5) {
      out.push(d.toISOString().slice(0, 10));
    }
    d = new Date(d.getTime() + 24 * 3600 * 1000);
  }
  return out;
}

export default function BusSchedule() {
  const [routes, setRoutes] = useState([]);
  const [active, setActive] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [mode, setMode] = useState("today"); // today | week
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const r = await api.get("/bus-routes");
    setRoutes(r.data || []);
    if (r.data?.[0] && !active) setActive(r.data[0]);
    const bb = await api.get("/bus-bookings");
    setBookings(bb.data || []);
  };

  useEffect(() => { load(); }, []);

  const dates = mode === "today" ? [new Date().toISOString().slice(0, 10)] : nextNWeekdays(5);

  async function book(route) {
    setSubmitting(true);
    try {
      const { data } = await api.post("/bus-bookings", {
        route_id: route.id,
        travel_dates: dates,
        boarding_stop: route.stops?.[0]?.name || "Departure",
        alighting_stop: route.stops?.slice(-1)[0]?.name || "Arrival",
      });
      const c = data?.count || 0;
      if (c === 0) {
        toast.warning("No new seats reserved", { description: (data?.skipped || []).map((s) => `${s.date}: ${s.reason}`).join(" · ") });
      } else if (c === 1) {
        toast.success(`Seat ${data.created[0].seat_number} confirmed for ${data.created[0].travel_date}`);
      } else {
        toast.success(`${c} weekday seats reserved ✓`, { description: `${data.created[0].travel_date} → ${data.created[c - 1].travel_date}` });
      }
      await load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-white">
      <MobileBrandHeader testId="bus-brand-header" />

      <div className="px-5 pt-2 pb-3">
        <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">Company Bus</p>
        <h1 className="font-display font-bold text-3xl text-slate-900">Routes & Schedules</h1>
      </div>

      <div className="px-5">
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-3">
          {routes.map((r) => (
            <button
              key={r.id}
              data-testid={`bus-route-chip-${r.id}`}
              onClick={() => setActive(r)}
              className={`flex-shrink-0 px-4 h-11 rounded-full text-sm font-semibold transition-all ${
                active?.id === r.id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              {r.name}
            </button>
          ))}
        </div>
      </div>

      {active && (
        <div className="px-5 mt-2 pb-32">
          <div className="bg-gradient-to-br from-primary to-primary-700 text-white rounded-3xl p-5 relative overflow-hidden">
            <div className="absolute -right-12 -top-12 w-40 h-40 bg-white/10 rounded-full" />
            <Bus size={32} className="text-white/80" />
            <h2 className="font-display font-bold text-2xl mt-3">{active.name}</h2>
            <p className="text-white/80 text-sm font-ar">{active.name_ar}</p>

            <div className="flex items-center justify-between gap-3 mt-5 bg-white/15 backdrop-blur rounded-xl px-3 sm:px-4 py-3 min-w-0">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-widest text-white/70 font-bold">Departure</p>
                <p className="font-display font-bold text-xl sm:text-2xl">{active.departure_time}</p>
                <p className="text-[11px] sm:text-xs text-white/80 truncate">{active.departure_address}</p>
              </div>
              <div className="text-white/40 text-xl font-light shrink-0">—</div>
              <div className="flex-1 min-w-0 text-right">
                <p className="text-[10px] uppercase tracking-widest text-white/70 font-bold">Arrival</p>
                <p className="font-display font-bold text-xl sm:text-2xl">{active.arrival_time}</p>
                <p className="text-[11px] sm:text-xs text-white/80 truncate">{active.arrival_address}</p>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between text-xs">
              <span className="flex items-center gap-1.5">
                <Users size={14} />
                {(active.vehicle?.capacity || 0) - (active.booked_seats || 0)} seats available
              </span>
              <span className="font-mono opacity-80">{active.vehicle?.plate_number}</span>
            </div>

            {/* Mode toggle */}
            <div className="mt-4 grid grid-cols-2 gap-2 p-1 bg-white/15 backdrop-blur rounded-xl" data-testid="bus-mode-toggle">
              <button
                data-testid="bus-mode-today"
                onClick={() => setMode("today")}
                className={`h-10 rounded-lg text-xs font-bold uppercase tracking-wider transition flex items-center justify-center gap-1.5 ${mode === "today" ? "bg-white text-primary" : "text-white/80"}`}
              >
                <CalendarDays size={14} /> Today
              </button>
              <button
                data-testid="bus-mode-week"
                onClick={() => setMode("week")}
                className={`h-10 rounded-lg text-xs font-bold uppercase tracking-wider transition flex items-center justify-center gap-1.5 ${mode === "week" ? "bg-white text-primary" : "text-white/80"}`}
              >
                <CalendarRange size={14} /> Whole Week
              </button>
            </div>

            {mode === "week" && (
              <div className="mt-3 flex gap-1.5 flex-wrap" data-testid="bus-week-preview">
                {dates.map((d) => (
                  <span key={d} className="text-[10px] font-mono bg-white/20 px-2 py-1 rounded-full">{d.slice(5)}</span>
                ))}
              </div>
            )}

            <button
              data-testid={`bus-book-${active.id}`}
              onClick={() => book(active)}
              disabled={submitting}
              className="mt-4 w-full h-12 rounded-xl bg-white text-primary font-display font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition disabled:opacity-60"
            >
              <Ticket size={18} /> {mode === "today" ? "Book a Seat (Today)" : `Reserve ${dates.length}-Day Pass`}
            </button>
          </div>

          {active.stops && (
            <div className="mt-5">
              <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-3">Stops</p>
              <div className="relative pl-6">
                <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-slate-200" />
                {active.stops.map((s, i) => (
                  <div key={i} className="relative pb-4">
                    <div className="absolute -left-6 top-1 w-4 h-4 rounded-full bg-white border-2 border-primary" />
                    <p className="font-semibold text-slate-900">{s.name}</p>
                    <p className="text-xs text-slate-500 font-mono flex items-center gap-1">
                      <Clock size={11} /> {s.time}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {bookings.length > 0 && (
            <div className="mt-6">
              <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-3">My Bus Bookings</p>
              <div className="space-y-2">
                {bookings.map((b) => (
                  <div key={b.id} data-testid={`bus-booking-${b.id}`} className="border border-slate-200 rounded-2xl p-4 flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-primary text-white flex items-center justify-center font-display font-bold">
                      {b.seat_number}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900 text-sm">{b.route?.name}</p>
                      <p className="text-xs text-slate-500 font-mono">{b.travel_date} · {b.route?.departure_time}</p>
                    </div>
                    <CheckCircle2 size={20} className="text-emerald-500" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
